/**
 * Client-side champion catalog for the selection UI — AUTO-DERIVED from content
 * (#239). Every champion shipped in `@pit/content` is automatically selectable
 * unless explicitly denylisted, so a new champion appears in the picker with
 * zero edits here (the old hand-maintained list silently dropped champions —
 * `afflictor` was missing, the test-only `reflecttest` was wrongly listed).
 *
 * SOURCE: the champion `{ id, slug, name, classColorKey }` fields are read at
 * BUILD TIME from the on-disk champion JSON
 * (`packages/content/data/champions/*.json`) via
 * `import.meta.glob`, mirroring the content-glob bridge in
 * `render/championSprites.ts` (#223) and the id→slug tables in `abilityIcons.ts`
 * (#72). We deliberately do NOT import the `@pit/content` runtime package into
 * the browser bundle — that would pull in `node:crypto` (its content hash) and
 * the loader. Only the raw champion JSON text inlines; Vite tree-shakes the rest.
 *
 * DENYLIST, NOT ALLOWLIST: a champion is playable by DEFAULT. It is hidden only
 * if its id is in `NOT_PLAYABLE`. This is the inversion the issue asks for —
 * new content is opt-out, not opt-in.
 *
 * FAIL-SAFE: the server independently rejects any champion id it doesn't know
 * (badMessage), so a stray catalog entry mis-seats nothing — it just fails safe.
 */

import { z } from "zod";
import {
  CHAMPION_ID_SCHEMA,
  CLASS_COLOR_KEY_SCHEMA,
  SLUG_SCHEMA,
  toChampionId,
  type ChampionId,
  type ClassColorKey,
} from "@pit/protocol";

export interface ChampionChoice {
  readonly id: ChampionId;
  readonly slug: string;
  readonly name: string;
  /** #617: per-champion identity color KEY, read straight off content — see
   *  `championColors.ts`. Absent ⇒ the hashed-palette fallback color. */
  readonly classColorKey: ClassColorKey | undefined;
}

/**
 * Champion ids that ship in content but are NOT player-selectable. Seeded with
 * `reflecttest` — a reflect-behaviour test fixture, never a real playable class.
 * `dummy` (#340) is now playable (#409) — target dummies are a legitimate pick,
 * not just a test fixture. Keyed by opaque champion id (#163; trailing comment
 * is the human slug), so a slug rename can't accidentally un-hide a test champion.
 */
export const NOT_PLAYABLE: ReadonlySet<ChampionId> = new Set<ChampionId>([
  // #576: deliberately literal, NOT slug-resolved — see the comment above:
  // keying by opaque id (not slug) is the point, so a slug rename can't
  // accidentally un-hide this test champion.
  // eslint-disable-next-line pit/no-hardcoded-champion-id
  toChampionId("chm_7Y7eTkpqWP"), // slug: reflecttest — test-only champion, not for play.
]);

/**
 * Minimal champion metadata schema — only the picker/presentation-relevant
 * fields. `z.object` (not `strictObject`) strips `stats`/`abilities`/etc., so
 * those never reach the client. `CHAMPION_ID_SCHEMA` brands `id` as
 * `ChampionId` at the boundary. `classColorKey` (#617) is the per-champion
 * identity color key, read straight off content instead of a hand-maintained
 * client-side table — see `championColors.ts`.
 */
const CHAMPION_META_SCHEMA = z.object({
  id: CHAMPION_ID_SCHEMA,
  slug: SLUG_SCHEMA,
  name: z.string().min(1).max(64),
  // #617: per-champion identity color key (optional — absent on a
  // config-authored champion with no curated color yet).
  classColorKey: CLASS_COLOR_KEY_SCHEMA.optional(),
});

/**
 * Build-time map of every on-disk champion JSON → its RAW text, then parsed down
 * to `{ id, slug, name, classColorKey }`. Same literal-`import.meta.glob` rule as
 * `championSprites.ts` (#181): the call MUST be the literal form for Vite's
 * static transform. Under vitest the same committed JSON resolves. A malformed
 * champion JSON throws here (loud) — but content is already validated by
 * `@pit/content`'s own tests + the server loader, so this can only fail if that
 * upstream gate was skipped.
 */
function loadContentChampions(): readonly ChampionChoice[] {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/champions/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out: ChampionChoice[] = [];
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const meta = CHAMPION_META_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.push({ id: meta.id, slug: meta.slug, name: meta.name, classColorKey: meta.classColorKey });
  }
  return out;
}

/** Every champion shipped in content (playable + denylisted). */
export const ALL_CONTENT_CHAMPIONS: readonly ChampionChoice[] = loadContentChampions();

/**
 * The default selection's opaque champion id. Explicitly THIS champion (NOT
 * `CHAMPIONS[0]`) — the catalog is sorted by name, so the first entry can
 * change as content grows, but the default must stay stable. A test asserts
 * this id is present in `CHAMPIONS` (playable).
 *
 * Keyed by opaque ID, NOT slug (#546 lesson) — mirrors `NOT_PLAYABLE`'s
 * existing id-keying rationale just above: a slug/name rename can't
 * desync a hardcoded id, because the id never changes. This file used to
 * resolve this via `championBySlugOrThrow("warrior")`, a slug-keyed lookup
 * added by #574 specifically to avoid hardcoded-id staleness. That lookup
 * does protect against a WRONG id (throws loudly if the slug doesn't
 * resolve) but not against a STALE slug: the Warrior -> Fighter rename
 * (#546 trial) changed the champion's `slug`/`name` while its `id` stayed
 * fixed, so the slug string literal passed into the lookup itself needed
 * hand-editing anyway — with a bigger blast radius, since 4 files across
 * the client depended on the `championBySlug`/`championBySlugOrThrow`
 * helpers existing at all (all 4 removed by #546; see git history if they're
 * needed again for an actual user-facing slug lookup, e.g. URL routing).
 * Resolving the id directly needs zero edits on any future rename.
 */
// eslint-disable-next-line pit/no-hardcoded-champion-id
export const DEFAULT_CHAMPION_ID: ChampionId = toChampionId("chm_BJ4RLXqiBG"); // slug: fighter (formerly warrior, #546)

/**
 * Selectable champions = every content champion MINUS the denylist, sorted by
 * `name` with an `id` tie-break. The sort makes the dropdown order stable and
 * deterministic across builds (glob key order is not contractually stable).
 */
export const CHAMPIONS: readonly ChampionChoice[] = ALL_CONTENT_CHAMPIONS.filter(
  (c) => !NOT_PLAYABLE.has(c.id),
).sort((a, b) => {
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  if (a.id !== b.id) return a.id < b.id ? -1 : 1;
  return 0;
});
